import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { AlertCircle, Loader2, TrendingUp, Share2, Repeat2, Quote } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

interface ViralityMetricsProps {
  contentId?: string;
}

export const ViralityMetrics = ({ contentId }: ViralityMetricsProps) => {
  const { data: metrics, isLoading, error } = useQuery({
    queryKey: ['virality-metrics', contentId],
    queryFn: async () => {
      if (!contentId) throw new Error("No content ID provided");
      
      const { data, error } = await supabase.functions.invoke('content-service', {
        body: { contentId }
      });
      
      if (error) throw error;
      return data;
    },
    enabled: !!contentId,
    refetchInterval: 30000
  });

  if (!contentId) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>No Content Selected</AlertTitle>
        <AlertDescription>
          Please select or generate content to view virality metrics.
        </AlertDescription>
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center p-6">
          <Loader2 className="h-6 w-6 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Error Loading Metrics</AlertTitle>
        <AlertDescription>
          Unable to load virality metrics. Please try again later.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5" />
          Content Virality
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Share2 className="h-4 w-4" />
              <span className="text-sm font-medium">Shares</span>
            </div>
            <span className="text-2xl font-bold">{metrics?.share_count || 0}</span>
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Repeat2 className="h-4 w-4" />
              <span className="text-sm font-medium">Reposts</span>
            </div>
            <span className="text-2xl font-bold">{metrics?.repost_count || 0}</span>
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Quote className="h-4 w-4" />
              <span className="text-sm font-medium">Citations</span>
            </div>
            <span className="text-2xl font-bold">{metrics?.citation_count || 0}</span>
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span>Virality Score</span>
            <span className="font-medium">
              {((metrics?.virality_score || 0) * 100).toFixed(1)}%
            </span>
          </div>
          <Progress 
            value={((metrics?.virality_score || 0) * 100)} 
            className="h-2"
          />
        </div>

        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span>Engagement Velocity</span>
            <span className="font-medium">
              {metrics?.engagement_velocity?.toFixed(2) || 0} per hour
            </span>
          </div>
          <Progress 
            value={Math.min((metrics?.engagement_velocity || 0) * 10, 100)} 
            className="h-2"
          />
        </div>
      </CardContent>
    </Card>
  );
};